import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Settings, LogOut, Phone } from 'lucide-react';
import { companyInfo } from '../mock';
import axios from 'axios';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isLoggedIn = !!localStorage.getItem('njk_admin_token');
  const adminRole = localStorage.getItem('njk_admin_role') || 'owner';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' }
  ];

  const adminLinks = [
    { name: 'Dashboard', path: '/admin/billing' },
    { name: 'Bookings', path: '/admin/bookings' },
    { name: 'Inventory', path: '/admin/inventory', ownerOnly: true },
    { name: 'Equipment', path: '/admin/equipment', ownerOnly: true },
    { name: 'Lorries', path: '/admin/lorries', ownerOnly: true },
    { name: 'Project Tracking', path: '/admin/profit-tracker', ownerOnly: true },
    { name: 'Contracts', path: '/admin/contracts', ownerOnly: true },
    { name: 'Payslips', path: '/admin/payslip', ownerOnly: true },
    { name: 'Payment Vouchers', path: '/admin/payment-vouchers', ownerOnly: true },
    { name: 'My Notes', path: '/admin/notes', ownerOnly: true },
    { name: 'Staff Accounts', path: '/admin/staff', ownerOnly: true },
    { name: 'GST Settings', path: '/admin/gst-settings', ownerOnly: true },
    { name: 'Change Password', path: '/admin/change-password' }
  ].filter((link) => !link.ownerOnly || adminRole === 'owner');

  const isActive = (path) => location.pathname === path;
  const isAdminPage = location.pathname.startsWith('/admin');
  const isPublicSignPage = location.pathname.startsWith('/sign/');
  const isLandingCard = location.pathname.startsWith('/card') || location.pathname === '/connect' || location.pathname.startsWith('/driver/');

  const handleLogout = () => {
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem('njk_admin_token');
    localStorage.removeItem('njk_admin_user');
    localStorage.removeItem('njk_admin_role');
    navigate('/');
    setShowAdminMenu(false);
  };

  return (
    isPublicSignPage || isLandingCard ? null : (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-burgundy-dark border-b border-gold/15 transition-shadow duration-300 ${
        isScrolled ? 'shadow-lg shadow-black/30' : ''
      }`}
      data-testid="site-header"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-2">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-16 md:h-20 w-auto object-contain"
            />
            <div className="hidden sm:block">
              <p className="font-display text-gold text-lg md:text-xl font-bold tracking-tight leading-none">NJK Logistics</p>
              <p className="text-[10px] md:text-xs text-gold-light/60 uppercase tracking-[0.25em] mt-1">Swift · Secure · Reliable</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-sm font-medium uppercase tracking-[0.15em] transition-colors relative group ${
                  isActive(link.path) ? 'text-gold' : 'text-white/80 hover:text-gold'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1.5 left-0 w-full h-px bg-gold transition-transform origin-left ${
                    isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-white/70 hover:text-gold transition-colors text-sm" data-testid="header-phone-link">
              <Phone size={15} className="text-gold" />
              {companyInfo.phone}
            </a>
            {isLoggedIn && (
            <div className="relative">
              <button
                onClick={() => setShowAdminMenu(!showAdminMenu)}
                data-testid="admin-menu-btn"
                className={`flex items-center gap-1 text-sm font-medium uppercase tracking-[0.1em] transition-colors ${
                  isAdminPage ? 'text-gold' : 'text-white/80 hover:text-gold'
                }`}
              >
                <Settings size={16} />
                Admin
              </button>
              {showAdminMenu && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-lg shadow-xl border border-burgundy/10 py-2 z-50 flex flex-col max-h-[70vh]">
                  <div className="overflow-y-auto flex-1">
                    {adminLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setShowAdminMenu(false)}
                        className={`block px-4 py-2 text-sm transition-colors ${
                          isActive(link.path)
                            ? 'bg-burgundy/5 text-burgundy font-semibold'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                  <hr className="my-1 border-gray-100" />
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors shrink-0"
                    data-testid="admin-logout-btn"
                  >
                    <LogOut size={14} className="inline mr-2" />Logout
                  </button>
                </div>
              )}
            </div>
            )}
            <Link to="/booking" data-testid="header-book-now-btn">
              <button className="bg-gold text-burgundy-deep font-semibold text-sm uppercase tracking-[0.1em] px-6 py-2.5 rounded-md hover:bg-gold-light transition-colors">
                Book Now
              </button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-gold hover:text-gold-light transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-burgundy-dark border-t border-gold/15 max-h-[calc(100vh-88px)] overflow-y-auto">
          <nav className="container mx-auto px-4 py-4 pb-16 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={`font-medium py-3 text-base uppercase tracking-[0.15em] border-b border-gold/10 transition-colors ${
                  isActive(link.path) ? 'text-gold' : 'text-white/85'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {isLoggedIn && (
            <div className="pt-4 mt-2">
              <p className="text-xs font-semibold text-gold/60 uppercase tracking-[0.2em] mb-2">Admin</p>
              {adminLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2.5 text-sm transition-colors ${
                    isActive(link.path) ? 'text-gold' : 'text-white/75'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                className="block font-medium py-3 text-red-400"
                data-testid="mobile-logout-btn"
              >
                Logout
              </button>
            </div>
            )}

            <Link to="/booking" onClick={() => setIsMobileMenuOpen(false)} className="mt-4">
              <button className="w-full bg-gold text-burgundy-deep font-semibold uppercase tracking-[0.1em] py-3.5 rounded-md hover:bg-gold-light transition-colors" data-testid="mobile-book-now-btn">
                Book Now
              </button>
            </Link>
          </nav>
        </div>
      )}
    </header>
    )
  );
};
